import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { logout } from '../actions/auth-actions';
import { APP_NAME } from '../utils';
import { pageList } from '../utils/gestion-access';
import Loader from '../components/loader';

const SIDEBAR_LINKS = [
  {
    id: 1,
    url: '/',
    icon: 'bi bi-house-door-fill',
    text: 'Tableau de bord',
    category: 'Comptabilité',
  },
  {
    id: 2,
    url: '/inventory',
    icon: 'bi bi-box-seam',
    text: 'Gestion des stocks',
    category: 'Comptabilité',
  },
  {
    id: 3,
    url: '/sales',
    icon: 'bi bi-cart-check',
    text: 'Gestion des ventes',
    category: 'Comptabilité',
  },
  {
    id: 4,
    url: '/rapports',
    icon: 'bi bi-journal-text',
    text: 'Rapports',
    category: 'Comptabilité',
  },
  {
    id: 5,
    url: '/finance-impots',
    icon: 'bi bi-cash-coin',
    text: 'Finance et Impôts',
    category: 'Comptabilité',
  },
];

const isLinkActive = (pathname, url) => {
  if (url === '/') return pathname === '/';
  return pathname === url || pathname.startsWith(`${url}/`);
};

const ComptableLayout = ({ children, loading = false }) => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);

  const firstName = user?.name?.split(' ')[0] ?? 'Utilisateur';
  const initial = firstName.charAt(0).toUpperCase();
  const roleLabel = user?.role ?? 'Comptable';

  const pages = pageList(user);
  const visibleLinks = SIDEBAR_LINKS.filter((link) => {
    if (!pages || user?.role === 'superAdmin') return true;
    if (link.url === '/inventory') return pages.includes('/inventory');
    if (link.url === '/sales') return pages.includes('/sales');
    if (link.url === '/rapports' || link.url === '/finance-impots') return pages.includes(link.url);
    return true;
  });
  const groupedLinks = visibleLinks.reduce((acc, link) => {
    if (!acc[link.category]) acc[link.category] = [];
    acc[link.category].push(link);
    return acc;
  }, {});

  const toggleSidebar = () => {
    setSidebarVisible((visible) => !visible);
  };

  const handleLogout = () => {
    Swal.fire({
      title: 'Êtes-vous sûr de vouloir vous déconnecter ?',
      text: 'Cette action vous déconnectera de votre compte.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#58cc02',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, déconnectez-moi',
      cancelButtonText: 'Annuler',
    }).then((result) => {
      if (result.isConfirmed) {
        setMenuOpen(false);
        dispatch(logout());
        navigate('/auth/login');
      }
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={sidebarVisible ? 'toggle-sidebar' : ''}>
      {loading && (
        <div style={{ zIndex: 9999 }} className="absolute top-0 left-0">
          <Loader />
        </div>
      )}
      <header id="header" className="header duo-header fixed-top">
        <div className="duo-header__inner">
          <div className="duo-header__left">
            <button
              type="button"
              className="duo-header__menu-btn"
              onClick={toggleSidebar}
              aria-label="Ouvrir ou fermer le menu"
            >
              <i className="bi bi-list" aria-hidden />
            </button>
            <Link to="/" className="duo-header__brand">
              <img src="/assets/img/logo.png" alt={APP_NAME} className="duo-header__logo" />
            </Link>
          </div>

          <div className="duo-header__right" ref={menuRef}>
            <button
              type="button"
              className="duo-header__profile-btn"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              <span className="duo-header__avatar" aria-hidden>
                {initial}
              </span>
              <span className="duo-header__user-name">{user?.name}</span>
              <i className={`bi bi-chevron-down duo-header__chevron${menuOpen ? ' is-open' : ''}`} aria-hidden />
            </button>

            {menuOpen && (
              <div className="duo-header__dropdown" role="menu">
                <div className="duo-header__dropdown-head">
                  <span className="duo-header__dropdown-avatar" aria-hidden>
                    {initial}
                  </span>
                  <div>
                    <p className="duo-header__dropdown-name">{user?.name}</p>
                    <p className="duo-header__dropdown-email">{user?.email}</p>
                  </div>
                </div>
                <ul className="duo-header__dropdown-list">
                  <li>
                    <Link
                      to="/edit-profile"
                      className="duo-header__dropdown-item"
                      onClick={() => setMenuOpen(false)}
                    >
                      <i className="bi bi-person" aria-hidden />
                      Mon compte
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/rapports"
                      className="duo-header__dropdown-item"
                      onClick={() => setMenuOpen(false)}
                    >
                      <i className="bi bi-journal-text" aria-hidden />
                      Rapports
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/finance-impots"
                      className="duo-header__dropdown-item"
                      onClick={() => setMenuOpen(false)}
                    >
                      <i className="bi bi-cash-coin" aria-hidden />
                      Finance et Impôts
                    </Link>
                  </li>
                  <li className="duo-header__dropdown-divider" />
                  <li>
                    <button
                      type="button"
                      className="duo-header__dropdown-item duo-header__dropdown-item--danger"
                      onClick={handleLogout}
                    >
                      <i className="bi bi-box-arrow-right" aria-hidden />
                      Déconnexion
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </header>

      <aside id="sidebar" className="sidebar duo-sidebar">
        <div className="duo-sidebar__profile">
          <div className="duo-sidebar__avatar" aria-hidden>
            {initial}
          </div>
          <div className="duo-sidebar__profile-text">
            <p className="duo-sidebar__name">{user?.name ?? firstName}</p>
            <p className="duo-sidebar__role">{roleLabel}</p>
          </div>
        </div>

        <p className="duo-sidebar__app">{APP_NAME}</p>

        <nav className="duo-sidebar__nav" aria-label="Menu comptable">
          {Object.entries(groupedLinks).map(([category, links]) => (
            <section key={category} className="duo-sidebar__section">
              <h3 className="duo-sidebar__category">{category}</h3>
              <ul className="duo-sidebar__list">
                {links.map((link) => {
                  const active = isLinkActive(location.pathname, link.url);
                  return (
                    <li key={link.id}>
                      <Link
                        to={link.url}
                        className={`duo-sidebar__link${active ? ' is-active' : ''}`}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="duo-sidebar__link-icon" aria-hidden>
                          <i className={link.icon} />
                        </span>
                        <span className="duo-sidebar__link-label">{link.text}</span>
                        {active ? <span className="duo-sidebar__link-dot" aria-hidden /> : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </nav>
      </aside>

      <main id="main" className="main duo-main-bg">
        <div className="duo-page">{children}</div>
      </main>
    </div>
  );
};

export default ComptableLayout;
